import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shiftApi } from '@/services/api';
import { X, Loader2, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { formatMoney } from '@/utils/currency';
import ResettleShiftModal from './ResettleShiftModal';

type Props = {
  onClose: () => void;
};

export default function MyShiftsModal({ onClose }: Props) {
  const [selectedRejectedShift, setSelectedRejectedShift] = useState<any>(null);

  const { data: rawShifts = [], isLoading } = useQuery({
    queryKey: ['myShifts'],
    queryFn: async () => {
      const res = await shiftApi.getMyShifts();
      return res.data.message || [];
    },
  });

  // Only show non-closed shifts — cashier only needs to act on pending/rejected ones
  const shifts = (rawShifts as any[]).filter((s: any) => s.status !== 'Closed');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Closed':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'Pending Approval':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'Rejected':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <CheckCircle2 className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Closed':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Closed</span>;
      case 'Pending Approval':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Pending Review</span>;
      case 'Rejected':
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Rejected - Needs Recount</span>;
      default:
        return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[90] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden animate-fade-in flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Pending Shifts</h3>
            <p className="text-sm text-gray-500">Shifts that need your attention — pending approval or requiring a recount.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-slate-50/50">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mb-3" />
              <p>Loading shift history...</p>
            </div>
          ) : shifts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <CheckCircle2 className="w-12 h-12 mb-3 opacity-20" />
              <p className="font-medium">All clear!</p>
              <p className="text-sm">No pending shifts requiring your attention.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {shifts.map((shift: any) => (
                <div key={shift.name} className={`bg-white rounded-xl border p-4 shadow-sm transition-colors ${
                  shift.status === 'Rejected' ? 'border-red-200 shadow-red-100/50' : 'border-slate-200'
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">{getStatusIcon(shift.status)}</div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm font-semibold text-slate-700">{shift.name}</span>
                          {getStatusBadge(shift.status)}
                        </div>
                        <div className="text-sm text-slate-500 mb-2">
                          {shift.shift_start?.substring(0, 16)} &rarr; {shift.shift_end?.substring(0, 16) || 'Ongoing'}
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                          <div><span className="text-slate-400">Sales:</span> <span className="font-medium text-slate-700">{formatMoney(shift.total_sales)}</span></div>
                          <div><span className="text-slate-400">Invoices:</span> <span className="font-medium text-slate-700">{shift.invoice_count}</span></div>
                          {(shift.status === 'Closed' || shift.status === 'Pending Approval') && (
                            <div>
                              <span className="text-slate-400">Variance:</span> 
                              <span className={`font-medium ml-1 ${shift.difference < 0 ? 'text-red-500' : shift.difference > 0 ? 'text-emerald-500' : 'text-slate-700'}`}>
                                {formatMoney(shift.difference)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {shift.status === 'Rejected' && (
                      <div className="sm:text-right shrink-0">
                        <button 
                          onClick={() => setSelectedRejectedShift(shift)}
                          className="w-full sm:w-auto bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <AlertCircle className="w-4 h-4" />
                          Resolve Discrepancy
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {selectedRejectedShift && (
        <ResettleShiftModal 
          shift={selectedRejectedShift} 
          onClose={() => setSelectedRejectedShift(null)} 
          onSuccess={() => setSelectedRejectedShift(null)}
        />
      )}
    </div>
  );
}
